// useBrowserKeys — owner-only Browser keys state for one app: the rows the app's own UI code
// asks for, and the mutations. Same plain useState/useEffect + `cancelled` guard convention as
// useAppSecrets (no react-query), and the same per-environment split.
//
// The difference from Secrets is the disclosure: a browser key's value IS returned, because it
// ships inside the bundle every visitor downloads — hiding it would only stop the owner
// checking that they pasted the publishable one.
import { useCallback, useEffect, useState } from "react";
import { listBrowserKeys, setBrowserKey, clearBrowserKey, type BrowserKeyRow, type SecretsEnv } from "../api";

export type UseBrowserKeys = {
  keys:   BrowserKeyRow[] | null;   // null while loading
  error:  string | null;
  reload: () => void;
  // Both resolve on success (the list refetches) or reject with the server's message, which
  // the panel shows inline. The value is sent as-is: never trimmed (see browser_keys_view).
  set:    (name: string, value: string) => Promise<void>;
  clear:  (name: string) => Promise<void>;
};

export function useBrowserKeys(slug: string, env: SecretsEnv = "dev"): UseBrowserKeys {
  const [keys, setKeys]   = useState<BrowserKeyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setKeys(null); setError(null);
    listBrowserKeys(slug, env)
      .then((r) => { if (!cancelled) setKeys(r.keys); })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [slug, env, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const set = useCallback(async (name: string, value: string) => { await setBrowserKey(slug, name, value, env); setNonce((n) => n + 1); }, [slug, env]);
  const clear = useCallback(async (name: string) => { await clearBrowserKey(slug, name, env); setNonce((n) => n + 1); }, [slug, env]);

  return { keys, error, reload, set, clear };
}
